/**
 * Map View Store
 *
 * Map-specific viewport and UI state (center, markers).
 * Separated from location-store because it's purely for the map widget rendering.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { MapMarker } from '@/types/map';
import type { MapCenter } from './helpers';
import { loadMapCenterFromStorage, saveMapCenterToStorage } from './helpers';

export interface MapViewState {
  mapCenter: MapCenter | null;
  markers: MapMarker[];

  // Actions
  setMapCenter: (center: MapCenter | null) => void;
  setMarkers: (markers: MapMarker[]) => void;
}

/**
 * Map view store instance
 *
 * Manages map viewport state independently of location data.
 *
 * Usage:
 * ```typescript
 * import { mapViewStore } from '@/features/map/stores/map-view-store';
 *
 * // Set map center (from geocoding search)
 * mapViewStore.getState().setMapCenter({ lat: 36.16, lng: -86.78, zoom: 12 });
 *
 * // Update markers
 * mapViewStore.getState().setMarkers(markers);
 * ```
 */
export const mapViewStore = create<MapViewState>()(
  subscribeWithSelector((set, _get) => ({
    // Initial state
    mapCenter: loadMapCenterFromStorage(), // Load from localStorage on init
    markers: [],

    /**
     * Set map center (for geocoding search results)
     *
     * @param center - Map center coordinates and zoom level (null to clear)
     */
    setMapCenter: (center) => {
      set({ mapCenter: center });
      // Persist to localStorage
      saveMapCenterToStorage(center);
    },

    /**
     * Set map markers
     *
     * @param markers - Array of marker objects with associated locations
     */
    setMarkers: (markers) => {
      set({ markers });
    },
  }))
);
